import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import VorionLogo from "../components/VorionLogo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      nav("/home");
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail || "Failed";
      // Backend returns 403 with the "awaiting approval" message on successful
      // self-registration. Treat that as a success state, not an error.
      if (status === 403 && /approval/i.test(detail)) {
        setSuccess(true);
      } else {
        setErr(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0E] p-6" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <VorionLogo variant="mark" size={48} testid="register-brand" />
        </div>
        <h2 className="text-3xl font-bold text-white tracking-tight mb-1">Request access</h2>
        <p className="text-sm text-zinc-400 mb-8">All users join the <span className="text-[#D4A968] font-mono">Vorion</span> workspace once a Global Admin approves your request.</p>

        {success ? (
          <div data-testid="register-pending" className="bg-[#D4A968]/10 border border-[#D4A968]/40 rounded-md p-5">
            <div className="text-[#D4A968] font-mono uppercase tracking-widest text-[10px] mb-1.5">
              Awaiting approval
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">You're on the list</h3>
            <p className="text-sm text-zinc-300 leading-relaxed mb-4">
              Your Vorion account for <span className="text-[#D4A968] font-mono">{form.email}</span> has
              been created. A Global Admin will review and approve your access shortly. You'll be able
              to sign in once that's done.
            </p>
            <Link to="/login" data-testid="register-success-login" className="inline-block text-xs bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold px-4 py-2 rounded-md">
              Go to login →
            </Link>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {[
            { k: "name", l: "Your name", t: "text" },
            { k: "email", l: "Email", t: "email" },
            { k: "password", l: "Password", t: "password" },
          ].map((f) => (
            <div key={f.k}>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">{f.l}</label>
              <input
                type={f.t} value={form[f.k]} onChange={set(f.k)} required
                data-testid={`register-${f.k}`}
                className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded-md px-3 py-2.5 text-sm text-white outline-none"
              />
            </div>
          ))}
          {err && <div data-testid="register-error" className="text-xs bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] rounded p-2.5">{err}</div>}
          <button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold py-3 rounded-md text-sm disabled:opacity-50">
            {loading ? "Submitting…" : "Request access →"}
          </button>
        </form>
        )}
        <div className="mt-6 text-xs text-zinc-500">
          Already onboarded? <Link to="/login" data-testid="login-link" className="text-[#D4A968]">Sign in →</Link>
        </div>
      </div>
    </div>
  );
}
